package com.evoapps.evoshift.widgets

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.selection.toggleable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.text.selection.LocalTextSelectionColors
import androidx.compose.foundation.text.selection.TextSelectionColors
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Search
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuAnchorType
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.MenuDefaults
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.RadioButton
import androidx.compose.material3.RadioButtonDefaults
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusDirection
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.date_field_placeholder
import evoshift.composeapp.generated.resources.date_format_invalid
import evoshift.composeapp.generated.resources.time_format_invalid
import kotlin.math.abs
import kotlin.math.round
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource

/*
 * Form controls for the navy dialogs and sheets (EvoDialog, EvoBottomSheet): light text, gold
 * accents, and a cursor and selection handles that stay visible on the dark surface.
 */

/** Error red that stays readable on navy (Material's error red is too dark there). */
val ErrorOnNavy = Color(0xFFFF8A80)

/** Navy surface of an open dropdown menu, a step lighter than the dialog under it (SH-196: the base's). */
private val MenuNavy: Color get() = com.evoapps.evoshift.ui.AppAppearance.palette.base.container

/** Gold handles and highlight: the app theme's navy ones vanish on a navy dialog. */
private val GoldSelections = com.evoapps.evoshift.ui.PerAppearance { p ->
    TextSelectionColors(handleColor = p.accent.bright, backgroundColor = p.accent.metallic.copy(alpha = 0.45f))
}
private val GoldSelectionColors: TextSelectionColors get() = GoldSelections.current

@Composable
private fun evoFieldColors() = OutlinedTextFieldDefaults.colors(
    cursorColor = LightGold,
    errorCursorColor = ErrorOnNavy,
    focusedBorderColor = DarkGold,
    unfocusedBorderColor = Color.White.copy(alpha = 0.28f),
    disabledBorderColor = Color.White.copy(alpha = 0.12f),
    errorBorderColor = ErrorOnNavy,
    focusedLabelColor = LightGold,
    unfocusedLabelColor = Color.White.copy(alpha = 0.6f),
    disabledLabelColor = Color.White.copy(alpha = 0.35f),
    errorLabelColor = ErrorOnNavy,
    focusedTextColor = Color.White,
    unfocusedTextColor = Color.White,
    disabledTextColor = Color.White.copy(alpha = 0.45f),
    errorTextColor = Color.White,
    focusedPlaceholderColor = Color.White.copy(alpha = 0.6f),
    unfocusedPlaceholderColor = Color.White.copy(alpha = 0.6f),
    focusedSupportingTextColor = Color.White.copy(alpha = 0.6f),
    unfocusedSupportingTextColor = Color.White.copy(alpha = 0.6f),
    errorSupportingTextColor = ErrorOnNavy,
    focusedTrailingIconColor = LightGold,
    unfocusedTrailingIconColor = Color.White.copy(alpha = 0.7f),
    errorTrailingIconColor = ErrorOnNavy,
    focusedLeadingIconColor = LightGold,
    unfocusedLeadingIconColor = Color.White.copy(alpha = 0.7f),
    // No fill (SH-83): the outlined field cuts its container out behind the floating label, so
    // on the dialogs' gradient a tinted container showed a dark rectangle behind every label
    // above a value (most visibly the dropdowns). The outline alone marks the field.
    focusedContainerColor = Color.Transparent,
    unfocusedContainerColor = Color.Transparent,
    errorContainerColor = Color.Transparent,
    disabledContainerColor = Color.Transparent,
    selectionColors = GoldSelectionColors
)

/**
 * The same field on a light (cream) card, e.g. a dashboard card: navy text and outline that stay
 * visible when the field is not focused, gold outline when it is.
 */
@Composable
private fun evoLightFieldColors() = OutlinedTextFieldDefaults.colors(
    cursorColor = DeepNavy,
    focusedBorderColor = DarkGold,
    unfocusedBorderColor = DeepNavy.copy(alpha = 0.35f),
    disabledBorderColor = DeepNavy.copy(alpha = 0.15f),
    focusedLabelColor = DeepNavy,
    unfocusedLabelColor = DeepNavy.copy(alpha = 0.65f),
    disabledLabelColor = DeepNavy.copy(alpha = 0.35f),
    focusedTextColor = DeepNavy,
    unfocusedTextColor = DeepNavy,
    disabledTextColor = DeepNavy.copy(alpha = 0.45f),
    focusedPlaceholderColor = DeepNavy.copy(alpha = 0.4f),
    unfocusedPlaceholderColor = DeepNavy.copy(alpha = 0.4f),
    focusedSupportingTextColor = DeepNavy.copy(alpha = 0.65f),
    unfocusedSupportingTextColor = DeepNavy.copy(alpha = 0.65f),
    focusedContainerColor = Color.White.copy(alpha = 0.75f),
    unfocusedContainerColor = Color.White.copy(alpha = 0.75f),
    errorContainerColor = Color.White.copy(alpha = 0.75f),
    disabledContainerColor = Color.Transparent,
    selectionColors = GoldSelectionColors
)

/**
 * The text field of every dialog and sheet. [error] replaces [supportingText] and colours the
 * field; [imeAction] defaults to Next so the keyboard walks through a form. [onLight] styles it
 * for a light (cream) card instead of a navy surface.
 */
@Composable
fun EvoTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    placeholder: String? = null,
    supportingText: String? = null,
    error: String? = null,
    keyboardType: KeyboardType = KeyboardType.Text,
    imeAction: ImeAction = ImeAction.Next,
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    capitalization: KeyboardCapitalization = KeyboardCapitalization.Sentences,
    singleLine: Boolean = true,
    minLines: Int = 1,
    readOnly: Boolean = false,
    enabled: Boolean = true,
    isPassword: Boolean = false,
    leadingIcon: ImageVector? = null,
    trailingIcon: @Composable (() -> Unit)? = null,
    suffix: String? = null,
    onLight: Boolean = false,
    /** Shown differently from what is stored (SH-262: the colon of a time field). */
    visualTransformation: VisualTransformation? = null
) {
    val focusManager = LocalFocusManager.current
    CompositionLocalProvider(LocalTextSelectionColors provides GoldSelectionColors) {
        rememberStableText(value, onValueChange).let { stable ->
            OutlinedTextField(
                value = stable.value,
                onValueChange = stable.onValueChange,
                label = { Text(label) },
                placeholder = placeholder?.let { { Text(it) } },
                supportingText = (error ?: supportingText)?.let { { Text(it) } },
                isError = error != null,
                // SH-98: Tab (Shift+Tab) leaves a multi-line box for the next (previous) field instead
                // of typing a tab, so a form is walked through with the keyboard alone.
                modifier = modifier.fillMaxWidth().then(
                    if (singleLine) Modifier else Modifier.onPreviewKeyEvent { event ->
                        if (event.key == Key.Tab && event.type == KeyEventType.KeyDown) {
                            focusManager.moveFocus(if (event.isShiftPressed) FocusDirection.Previous else FocusDirection.Next)
                            true
                        } else event.key == Key.Tab
                    }
                ),
                keyboardOptions = KeyboardOptions(
                    keyboardType = keyboardType,
                    imeAction = imeAction,
                    capitalization = if (keyboardType == KeyboardType.Text) capitalization else KeyboardCapitalization.None
                ),
                keyboardActions = keyboardActions,
                singleLine = singleLine,
                minLines = minLines,
                readOnly = readOnly,
                enabled = enabled,
                visualTransformation = visualTransformation ?: if (isPassword) PasswordVisualTransformation() else VisualTransformation.None,
                leadingIcon = leadingIcon?.let { { Icon(it, contentDescription = null) } },
                trailingIcon = trailingIcon,
                suffix = suffix?.let { { Text(it, color = (if (onLight) DeepNavy else Color.White).copy(alpha = 0.6f)) } },
                shape = RoundedCornerShape(12.dp),
                colors = if (onLight) evoLightFieldColors() else evoFieldColors()
            )
        }
    }
}

/**
 * SH-213: the outline colours of a search field on a surface: a visible edge at rest (the
 * surface's secondary text colour, 4.5:1 on it) and the surface's accent while focused (with
 * Material's thicker focused outline). ThemeContrastTest checks both at least 3:1 (WCAG 1.4.11)
 * on every surface in all nine SH-196 combinations.
 */
data class SearchFieldColours(val border: Color, val focusedBorder: Color, val container: Color)

fun searchFieldColours(on: OnColours): SearchFieldColours = SearchFieldColours(
    border = on.onSurfaceVariant,
    focusedBorder = on.accent,
    // White on the cream screens (as before); none on the base-coloured surfaces.
    container = if (on.dark) Color.Transparent else Color.White
)

/**
 * SH-213: a search box above a list (the team's "Search"): a rounded outlined field with the
 * search icon and a [placeholder], its edge visible on the surface, the accent while focused. It
 * replaces a borderless white pill that hardly showed on the cream screens.
 */
@Composable
fun EvoSearchField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    modifier: Modifier = Modifier
) {
    val on = LocalOnColours.current
    val colours = searchFieldColours(on)
    rememberStableText(value, onValueChange).let { stable ->
        OutlinedTextField(
            value = stable.value,
            onValueChange = stable.onValueChange,
            placeholder = { Text(placeholder) },
            leadingIcon = { Icon(Icons.Default.Search, contentDescription = null) },
            modifier = modifier,
            shape = RoundedCornerShape(50),
            singleLine = true,
            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = colours.focusedBorder,
                unfocusedBorderColor = colours.border,
                focusedContainerColor = colours.container,
                unfocusedContainerColor = colours.container,
                cursorColor = on.onSurface,
                focusedTextColor = on.onSurface,
                unfocusedTextColor = on.onSurface,
                focusedPlaceholderColor = on.onSurfaceVariant,
                unfocusedPlaceholderColor = on.onSurfaceVariant,
                focusedLeadingIconColor = colours.focusedBorder,
                unfocusedLeadingIconColor = on.onSurfaceVariant
            )
        )
    }
}

/**
 * A clock time typed as `HH:mm` (also `8:30`, `830`, `0830`, `8`); null when it is not one.
 * Returns the normalised `HH:mm`.
 */
fun parseClockTime(input: String): String? {
    val text = input.trim()
    if (text.isEmpty()) return null
    val (hours, minutes) = when {
        ':' in text -> text.substringBefore(':') to text.substringAfter(':')
        text.length <= 2 -> text to "00"
        text.length in 3..4 -> text.dropLast(2) to text.takeLast(2)
        else -> return null
    }
    if (hours.isEmpty() || hours.length > 2 || minutes.length != 2) return null
    if (!hours.all { it.isDigit() } || !minutes.all { it.isDigit() }) return null
    val h = hours.toInt()
    val m = minutes.toInt()
    if (h !in 0..23 || m !in 0..59) return null
    return "${h.toString().padStart(2, '0')}:${minutes}"
}

/** An ISO date (`2026-02-01`); null when it is not one. */
fun parseIsoDate(input: String): LocalDate? = runCatching { LocalDate.parse(input.trim()) }.getOrNull()

/** A decimal typed with a dot or a comma; null when it is not a number. */
fun parseDecimal(input: String): Double? = input.trim().replace(',', '.').replace(" ", "").toDoubleOrNull()

/**
 * [value] as a field's starting text: no thousands separators (so [parseDecimal] reads it back),
 * at most [maxDigits] decimals, no trailing zeros (`5000`, `25.2`).
 */
fun formatEditable(value: Double, maxDigits: Int = 2): String {
    var factor = 1L
    repeat(maxDigits) { factor *= 10 }
    val scaled = round(value * factor).toLong()
    val sign = if (scaled < 0) "-" else ""
    val absolute = abs(scaled)
    val fraction = (absolute % factor).toString().padStart(maxDigits, '0').trimEnd('0')
    return sign + (absolute / factor) + if (fraction.isEmpty()) "" else ".$fraction"
}

/**
 * A clock time field. Only digits are typed: the colon appears by itself after the hours and
 * Delete removes digits, never the colon (SH-262: "1230" shows as 12:30, "9" as 09:). It reports
 * "HH:MM" once four digits are in, the hour alone ("12", read as 12:00) or an unfinished "12:3"
 * while typing; leaving the field turns an hour alone into "12:00". Shows the format error under
 * the field once something invalid is typed (not while a time is half typed; [showErrors] forces
 * it, e.g. after a Save attempt with an empty field); [error] overrides it.
 */
@Composable
fun EvoTimeField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    placeholder: String = "08:00",
    optional: Boolean = false,
    showErrors: Boolean = false,
    error: String? = null,
    imeAction: ImeAction = ImeAction.Next,
    /** SH-165: what counts as a time here (availability also takes "24:00" as an end). */
    isValid: (String) -> Boolean = { parseClockTime(it) != null },
    /** On a light card instead of a navy dialog. */
    onLight: Boolean = false
) {
    val formatError = stringResource(Res.string.time_format_invalid)
    val field = remember { TimeFieldDigits(value) }
    field.sync(value)
    var focused by remember { mutableStateOf(false) }
    val invalid = when {
        value.isBlank() -> showErrors && !optional
        field.digits.length == 3 && focused -> false
        else -> !isValid(value)
    }
    fun report(digits: String) {
        val text = timeFromDigits(digits)
        field.digits = digits
        field.reported = text
        onValueChange(text)
    }
    EvoTextField(
        value = field.digits,
        onValueChange = { new -> report(clockDigits(new)) },
        label = label,
        modifier = modifier.onFocusChanged { state ->
            // An hour alone becomes a whole time once the field is left.
            if (focused && !state.isFocused && field.digits.length in 1..2) report(field.digits.padStart(2, '0') + "00")
            focused = state.isFocused
        },
        placeholder = placeholder,
        error = error ?: formatError.takeIf { invalid },
        keyboardType = KeyboardType.Number,
        imeAction = imeAction,
        onLight = onLight,
        visualTransformation = TimeColonTransformation
    )
}

/**
 * The digits of a typed time, at most four; a first digit above 2 is an hour of one digit
 * ("9" → "09"). A time with a colon ("8:00") keeps its minutes.
 */
internal fun clockDigits(input: String): String {
    val hasColon = ':' in input
    val digits = if (hasColon) input.substringBefore(':').filter { it.isDigit() }.takeLast(2).padStart(2, '0') +
        input.substringAfter(':').filter { it.isDigit() }.take(2)
    else input.filter { it.isDigit() }
    val hour = if (!hasColon && digits.isNotEmpty() && digits[0] > '2') "0$digits" else digits
    return hour.take(4)
}

/** What a time field reports for its [digits]: "12:30", the hour alone "12", or an unfinished "12:3". */
internal fun timeFromDigits(digits: String): String =
    if (digits.length <= 2) digits else digits.take(2) + ":" + digits.drop(2)

/** Shows a time field's digits with the colon after the hours ("12" → "12:", "1230" → "12:30"). */
internal object TimeColonTransformation : VisualTransformation {
    override fun filter(text: AnnotatedString): TransformedText {
        val digits = text.text
        if (digits.length < 2) return TransformedText(text, OffsetMapping.Identity)
        val shown = digits.take(2) + ":" + digits.drop(2)
        return TransformedText(AnnotatedString(shown), object : OffsetMapping {
            override fun originalToTransformed(offset: Int) = if (offset < 2) offset else offset + 1
            override fun transformedToOriginal(offset: Int) = (if (offset <= 2) offset else offset - 1).coerceIn(0, digits.length)
        })
    }
}

/**
 * What a time field holds ([digits], "1230") beside what it last told the caller ([reported],
 * "12:30"). A caller's value other than that (a reset, a loaded time) replaces the digits.
 */
private class TimeFieldDigits(initial: String) {
    var digits by mutableStateOf(clockDigits(initial))
    var reported: String = initial

    fun sync(value: String) {
        if (value == reported) return
        reported = value
        digits = clockDigits(value)
    }
}

/**
 * A date field (SH-166). It takes a date the way people write it ("3.11", "03.11.2026",
 * "3/11/2026", "20261103", "2026-11-03", see [parseDateEntry]) and reports the ISO date
 * (`2026-11-03`) to [onValueChange] as soon as the text is a whole date, so what depends on it
 * follows while typing; until then it reports the text itself (which [parseIsoDate] refuses).
 * Leaving the field (or Next / Done) shows the ISO date in it; the date written out ("3 listopada
 * 2026") is under it. A missing year comes from [missingYear]. The format error shows for a day
 * that cannot exist at once, for an unfinished date once the field is left ([showErrors] also
 * for an empty one); [error] overrides it.
 *
 * The calendar icon opens the date picker ([EvoDatePickerDialog], only [allowed] days), or calls
 * [onOpenPicker] instead (a period's range picker, [EvoDateRangeField]).
 *
 * Keyboard: [KeyboardType.Decimal]. The plain number pad of iOS ([KeyboardType.Number]) has no
 * separator at all; the decimal pad has the locale's decimal separator ("," in Polish, "." in
 * English), which the field takes as a date separator, and digits alone ("20261103") work on
 * any pad. Android's number keyboards and physical keyboards have ".", "-" and "/" too.
 */
@Composable
fun EvoDateField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    showErrors: Boolean = false,
    error: String? = null,
    imeAction: ImeAction = ImeAction.Next,
    missingYear: MissingYear = MissingYear.ThisYear,
    allowed: ClosedRange<LocalDate>? = null,
    onOpenPicker: (() -> Unit)? = null
) {
    val formatError = stringResource(Res.string.date_format_invalid)
    val today = AppClock.today()
    val field = remember { DateFieldText(value) }
    field.sync(value)
    var focused by remember { mutableStateOf(false) }
    var picking by remember { mutableStateOf(false) }
    val entry = parseDateEntry(field.text, today, missingYear)
    val invalid = when (entry) {
        DateEntry.Empty -> showErrors
        DateEntry.Incomplete -> !focused
        DateEntry.Impossible -> true
        is DateEntry.Complete -> false
    }
    fun normalise() {
        (parseDateEntry(field.text, today, missingYear) as? DateEntry.Complete)?.let { field.text = it.date.toString() }
    }
    EvoTextField(
        value = field.text,
        onValueChange = { new ->
            val text = new.filter(::isDateCharacter).take(10)
            field.text = text
            val report = (parseDateEntry(text, today, missingYear) as? DateEntry.Complete)?.date?.toString() ?: text
            field.reported = report
            onValueChange(report)
        },
        label = label,
        modifier = modifier.onFocusChanged { state ->
            if (focused && !state.isFocused) normalise()
            focused = state.isFocused
        },
        placeholder = stringResource(Res.string.date_field_placeholder),
        supportingText = (entry as? DateEntry.Complete)?.let { longDate(it.date) },
        error = error ?: formatError.takeIf { invalid },
        keyboardType = KeyboardType.Decimal,
        imeAction = imeAction,
        keyboardActions = KeyboardActions(
            onNext = { normalise(); defaultKeyboardAction(ImeAction.Next) },
            onDone = { normalise(); defaultKeyboardAction(ImeAction.Done) }
        ),
        trailingIcon = { CalendarIconButton(onClick = { onOpenPicker?.invoke() ?: run { picking = true } }) }
    )
    if (picking) {
        EvoDatePickerDialog(
            initial = (entry as? DateEntry.Complete)?.date,
            title = label,
            allowed = allowed,
            onPick = { date ->
                field.text = date.toString()
                field.reported = date.toString()
                onValueChange(date.toString())
                picking = false
            },
            onDismiss = { picking = false }
        )
    }
}

/**
 * What a date field shows ([text], e.g. "3.11" while typing) beside what it last told the caller
 * ([reported], "2026-11-03"). A caller's value other than that (a reset, a picked or loaded date)
 * replaces the text.
 */
private class DateFieldText(initial: String) {
    var text by mutableStateOf(initial)
    var reported: String = initial

    fun sync(value: String) {
        if (value == reported) return
        reported = value
        text = value
    }
}

/** Small gold capitals that open a group of fields inside a dialog. */
@Composable
fun DialogSectionLabel(text: String, modifier: Modifier = Modifier) {
    Text(
        text = text.uppercase(),
        modifier = modifier.padding(top = 4.dp),
        style = MaterialTheme.typography.labelMedium,
        color = DarkGold,
        fontWeight = FontWeight.Bold,
        letterSpacing = 1.2.sp
    )
}

/** A labelled group of fields: [DialogSectionLabel] and the fields 12dp apart. */
@Composable
fun DialogSection(title: String?, modifier: Modifier = Modifier, content: @Composable ColumnScope.() -> Unit) {
    Column(modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        title?.let { DialogSectionLabel(it) }
        content()
    }
}

@Composable
private fun RowTexts(label: String, supporting: String?, modifier: Modifier) {
    Column(modifier) {
        Text(label, color = Color.White, style = MaterialTheme.typography.bodyLarge)
        supporting?.let {
            Text(it, color = Color.White.copy(alpha = 0.6f), style = MaterialTheme.typography.bodySmall)
        }
    }
}

/** A checkbox whose whole row toggles it. */
@Composable
fun EvoCheckboxRow(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    supporting: String? = null,
    enabled: Boolean = true
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = 48.dp)
            .handCursor()
            .toggleable(value = checked, enabled = enabled, role = Role.Checkbox, onValueChange = onCheckedChange)
            .padding(vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Checkbox(
            checked = checked,
            onCheckedChange = null,
            enabled = enabled,
            colors = CheckboxDefaults.colors(
                checkedColor = DarkGold,
                uncheckedColor = Color.White.copy(alpha = 0.55f),
                checkmarkColor = DeepNavy
            )
        )
        Spacer(Modifier.width(14.dp))
        RowTexts(label, supporting, Modifier.weight(1f))
    }
}

/** A switch whose whole row toggles it, the label on the left. */
@Composable
fun EvoSwitchRow(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    label: String,
    modifier: Modifier = Modifier,
    supporting: String? = null,
    enabled: Boolean = true
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = 48.dp)
            .handCursor()
            .toggleable(value = checked, enabled = enabled, role = Role.Switch, onValueChange = onCheckedChange)
            .padding(vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        RowTexts(label, supporting, Modifier.weight(1f))
        Spacer(Modifier.width(12.dp))
        Switch(
            checked = checked,
            onCheckedChange = null,
            enabled = enabled,
            colors = SwitchDefaults.colors(
                checkedThumbColor = DeepNavy,
                checkedTrackColor = DarkGold,
                checkedBorderColor = DarkGold,
                uncheckedThumbColor = Color.White.copy(alpha = 0.75f),
                uncheckedTrackColor = Color.White.copy(alpha = 0.08f),
                uncheckedBorderColor = Color.White.copy(alpha = 0.4f)
            )
        )
    }
}

/** One choice of a set; [description] adds a second line under each option. */
@Composable
fun <T> EvoRadioGroup(
    options: List<T>,
    selected: T?,
    onSelect: (T) -> Unit,
    optionLabel: @Composable (T) -> String,
    modifier: Modifier = Modifier,
    description: (@Composable (T) -> String?)? = null,
    horizontal: Boolean = false
) {
    @Composable
    fun Option(option: T, rowModifier: Modifier) {
        val isSelected = option == selected
        Row(
            modifier = rowModifier
                .heightIn(min = 48.dp)
                .handCursor()
                .selectable(selected = isSelected, role = Role.RadioButton, onClick = { onSelect(option) })
                .padding(vertical = 4.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            RadioButton(
                selected = isSelected,
                onClick = null,
                colors = RadioButtonDefaults.colors(
                    selectedColor = DarkGold,
                    unselectedColor = Color.White.copy(alpha = 0.55f)
                )
            )
            Spacer(Modifier.width(14.dp))
            Column {
                Text(
                    optionLabel(option),
                    color = Color.White,
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Normal
                )
                description?.invoke(option)?.let {
                    Text(it, color = Color.White.copy(alpha = 0.6f), style = MaterialTheme.typography.bodySmall)
                }
            }
        }
    }
    if (horizontal) {
        Row(modifier.fillMaxWidth().selectableGroup(), horizontalArrangement = Arrangement.spacedBy(24.dp)) {
            options.forEach { Option(it, Modifier) }
        }
    } else {
        Column(modifier.fillMaxWidth().selectableGroup()) {
            options.forEach { Option(it, Modifier.fillMaxWidth()) }
        }
    }
}

/**
 * A read-only field that opens a menu of [options]. [placeholder] shows while nothing is
 * selected; the menu is navy with the selected option in gold. [optionDetail] adds a line under
 * an option in the menu (e.g. a person's availability that day).
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun <T> EvoDropdown(
    label: String,
    options: List<T>,
    selected: T?,
    onSelected: (T) -> Unit,
    optionLabel: @Composable (T) -> String,
    modifier: Modifier = Modifier,
    placeholder: String? = null,
    supportingText: String? = null,
    error: String? = null,
    enabled: Boolean = true,
    optionDetail: (@Composable (T) -> Unit)? = null,
    /** On a light (cream) surface instead of a navy dialog. */
    onLight: Boolean = false
) {
    var expanded by remember { mutableStateOf(false) }
    ExposedDropdownMenuBox(
        expanded = expanded,
        onExpandedChange = { if (enabled) expanded = it },
        modifier = modifier.fillMaxWidth()
    ) {
        // SH-130: the anchor's tap (press, then release) is cancelled when anything consumes a
        // pointer move in between, and a read-only text field takes every move as a text
        // selection drag. A mouse or trackpad nearly always moves a pixel during a click, so on
        // the web and the desktop only the arrow (outside the text) opened the menu. The shield
        // over the field keeps the pointer away from the text field without consuming anything;
        // the anchor around both still sees the whole click and toggles the menu once.
        Box(Modifier.menuAnchor(ExposedDropdownMenuAnchorType.PrimaryNotEditable, enabled)) {
            EvoTextField(
                value = selected?.let { optionLabel(it) } ?: "",
                onValueChange = {},
                label = label,
                placeholder = placeholder,
                supportingText = supportingText,
                error = error,
                readOnly = true,
                enabled = enabled,
                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
                onLight = onLight
            )
            if (enabled) Box(Modifier.matchParentSize().handCursor().pointerShield())
        }
        ExposedDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            containerColor = MenuNavy,
            shape = RoundedCornerShape(12.dp)
        ) {
            options.forEach { option ->
                val isSelected = option == selected
                DropdownMenuItem(
                    text = {
                        Column {
                            Text(
                                optionLabel(option),
                                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Normal
                            )
                            optionDetail?.invoke(option)
                        }
                    },
                    onClick = {
                        onSelected(option)
                        expanded = false
                    },
                    colors = MenuDefaults.itemColors(textColor = if (isSelected) LightGold else Color.White),
                    modifier = Modifier.handCursor(),
                    contentPadding = ExposedDropdownMenuDefaults.ItemContentPadding
                )
            }
        }
    }
}

/**
 * Takes the pointer from whatever lies under it without consuming the events, so ancestors still
 * see every press, move and release as they are (see [EvoDropdown]).
 */
private fun Modifier.pointerShield(): Modifier = pointerInput(Unit) {
    awaitPointerEventScope {
        while (true) awaitPointerEvent(PointerEventPass.Initial)
    }
}

/** Two to four mutually exclusive choices side by side. */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun <T> EvoSegmented(
    options: List<T>,
    selected: T,
    onSelect: (T) -> Unit,
    optionLabel: @Composable (T) -> String,
    modifier: Modifier = Modifier
) {
    SingleChoiceSegmentedButtonRow(modifier = modifier.fillMaxWidth()) {
        options.forEachIndexed { index, option ->
            SegmentedButton(
                selected = option == selected,
                onClick = { onSelect(option) },
                shape = SegmentedButtonDefaults.itemShape(index = index, count = options.size, baseShape = RoundedCornerShape(12.dp)),
                colors = SegmentedButtonDefaults.colors(
                    activeContainerColor = DarkGold,
                    activeContentColor = DeepNavy,
                    activeBorderColor = DarkGold,
                    inactiveContainerColor = Color.Transparent,
                    inactiveContentColor = Color.White,
                    inactiveBorderColor = Color.White.copy(alpha = 0.3f)
                ),
                modifier = Modifier.handCursor()
            ) {
                Text(optionLabel(option), fontWeight = if (option == selected) FontWeight.Bold else FontWeight.Normal)
            }
        }
    }
}
